/**
 * Ant Design Theme Configuration
 * Purple Theme System - Based on UX Design Tokens
 *
 * IMPORTANT: Primary color is PURPLE (purpleColors.purple6), not blue!
 * All interactive elements, links, and info components use purple.
 */

import { ThemeConfig } from 'antd';
import { purpleColors, purpleColorsDark, statusColors, neutralColors } from './colors';
import {
  sizeTokens,
  controlHeightTokens,
  marginTokens,
  paddingTokens,
  typographyTokens,
  borderRadiusTokens,
  radiusTokens,
  shadowTokens,
  shadowTokensDark,
} from './tokens';

/**
 * Light Theme Configuration
 * Based on design system specifications
 */
export const lightTheme: ThemeConfig = {
  token: {
    // ============================================
    // PURPLE PRIMARY COLOR SYSTEM
    // ============================================
    colorPrimary: purpleColors.purple6,  // #7257F8 - Main brand color

    // Primary color variants (Ant Design auto-generates, but we specify for consistency)
    colorPrimaryBg: purpleColors.purple1,           // Light purple background
    colorPrimaryBgHover: purpleColors.purple2,      // Hover background
    colorPrimaryBorder: purpleColors.purple3,       // Border color
    colorPrimaryBorderHover: purpleColors.purple4,  // Border hover
    colorPrimaryHover: purpleColors.purple5,        // Hover state
    colorPrimaryActive: purpleColors.purple7,       // Active/pressed state
    colorPrimaryTextHover: purpleColors.purple5,    // Text hover
    colorPrimaryText: purpleColors.purple6,         // Text color
    colorPrimaryTextActive: purpleColors.purple7,   // Text active

    // ============================================
    // INFO & LINK COLORS → PURPLE (NOT BLUE!)
    // ============================================
    // Based on Screenshot 57 - All info and link colors map to purple
    colorInfo: purpleColors.purple6,
    colorInfoBg: purpleColors.purple1,
    colorInfoBgHover: purpleColors.purple2,
    colorInfoBorder: purpleColors.purple3,
    colorInfoBorderHover: purpleColors.purple4,
    colorInfoHover: purpleColors.purple5,
    colorInfoActive: purpleColors.purple7,
    colorInfoTextHover: purpleColors.purple5,
    colorInfoText: purpleColors.purple6,
    colorInfoTextActive: purpleColors.purple7,

    colorLink: purpleColors.purple6,          // Hyperlinks are purple
    colorLinkHover: purpleColors.purple5,     // Link hover
    colorLinkActive: purpleColors.purple7,    // Link active/visited

    // ============================================
    // STATUS COLORS (Unchanged)
    // ============================================
    colorSuccess: statusColors.success,
    colorWarning: statusColors.warning,
    colorError: statusColors.error,

    // ============================================
    // BACKGROUND COLORS - Light Mode
    // ============================================
    colorBgContainer: neutralColors.light.bgContainer,      // #ffffff
    colorBgElevated: neutralColors.light.bgElevated,        // #ffffff
    colorBgLayout: neutralColors.light.bgLayout,            // #f5f5f5
    colorBgSpotlight: neutralColors.light.bgSpotlight,      // #fafafa

    // ============================================
    // TEXT COLORS - Light Mode
    // ============================================
    colorText: neutralColors.light.textPrimary,           // rgba(0, 0, 0, 0.88)
    colorTextSecondary: neutralColors.light.textSecondary, // rgba(0, 0, 0, 0.65)
    colorTextTertiary: neutralColors.light.textTertiary,  // rgba(0, 0, 0, 0.45)
    colorTextQuaternary: neutralColors.light.textQuaternary, // rgba(0, 0, 0, 0.25)

    // ============================================
    // BORDER COLORS
    // ============================================
    colorBorder: neutralColors.light.border,              // #d9d9d9
    colorBorderSecondary: neutralColors.light.borderSecondary, // #f0f0f0

    // ============================================
    // TYPOGRAPHY - Based on Screenshot 58
    // ============================================
    fontSize: typographyTokens.sm.size,                    // 14px base

    // Heading sizes - EXACT specifications from design system
    fontSizeHeading1: typographyTokens.h1.size,           // 38px
    fontSizeHeading2: typographyTokens.h2.size,           // 30px
    fontSizeHeading3: typographyTokens.h3.size,           // 24px
    fontSizeHeading4: typographyTokens.h4.size,           // 20px
    fontSizeHeading5: typographyTokens.h5.size,           // 16px

    // Line heights
    lineHeight: typographyTokens.sm.lineHeight / typographyTokens.sm.size,  // 22/14 = 1.57
    lineHeightHeading1: typographyTokens.h1.lineHeight / typographyTokens.h1.size, // 46/38 = 1.21
    lineHeightHeading2: typographyTokens.h2.lineHeight / typographyTokens.h2.size, // 38/30 = 1.27
    lineHeightHeading3: typographyTokens.h3.lineHeight / typographyTokens.h3.size, // 32/24 = 1.33
    lineHeightHeading4: typographyTokens.h4.lineHeight / typographyTokens.h4.size, // 28/20 = 1.40
    lineHeightHeading5: typographyTokens.h5.lineHeight / typographyTokens.h5.size, // 24/16 = 1.50

    // Other font sizes
    fontSizeSM: typographyTokens.sm.size,    // 14px
    fontSizeLG: typographyTokens.lg.size,    // 18px
    fontSizeXL: typographyTokens.h4.size,    // 20px

    // ============================================
    // SPACING - Based on Screenshot 60
    // ============================================
    // Padding
    paddingXXS: paddingTokens.paddingXXS,    // 4px
    paddingXS: paddingTokens.paddingXS,      // 8px
    paddingSM: paddingTokens.paddingSM,      // 12px
    padding: paddingTokens.padding,          // 16px
    paddingMD: paddingTokens.paddingMD,      // 20px
    paddingLG: paddingTokens.paddingLG,      // 24px
    paddingXL: paddingTokens.paddingXL,      // 32px

    // Margin
    marginXXS: marginTokens.marginXXS,       // 4px
    marginXS: marginTokens.marginXS,         // 8px
    marginSM: marginTokens.marginSM,         // 12px
    margin: marginTokens.margin,             // 16px
    marginMD: marginTokens.marginMD,         // 20px
    marginLG: marginTokens.marginLG,         // 24px
    marginXL: marginTokens.marginXL,         // 32px
    marginXXL: marginTokens.marginXXL,       // 48px

    // ============================================
    // SIZING - Based on Screenshot 59
    // ============================================
    sizeXXS: sizeTokens.sizeXXS,             // 8px
    sizeXS: sizeTokens.sizeXS,               // 12px
    sizeSM: sizeTokens.sizeSM,               // 16px
    size: sizeTokens.size,                   // 20px
    sizeMS: sizeTokens.sizeMS,               // 22px
    sizeMD: sizeTokens.sizeMD,               // 24px
    sizeLG: sizeTokens.sizeLG,               // 28px
    sizeXL: sizeTokens.sizeXL,               // 36px
    sizeXXL: sizeTokens.sizeXXL,             // 52px

    // ============================================
    // CONTROL HEIGHTS — the house control scale (`controlHeightTokens`)
    // ============================================
    // NOT the `sizeTokens` icon/spacing scale. AntD derives a control's own
    // vertical padding from these seeds
    // (`antd/es/input/style/token.js`: paddingBlockLG =
    //  max(ceil((controlHeightLG - fontSizeLG x lineHeightLG) / 2 x 10) / 10 - lineWidth, 0)),
    // so seeding them from the icon scale (24 / 16 / 28) left AntD with
    // paddingBlockLG === 0 — zero room to centre a large control — and every
    // control 8-16px shorter than the 44px the design system asks for.
    // `controlHeightLG` maps to the XL rung because the house's "large form
    // control" IS 44px (`formStyles.inputStyle`, CLAUDE.md "size='large' (44px)").
    controlHeight: controlHeightTokens.controlHeight,       // 36px
    controlHeightSM: controlHeightTokens.controlHeightSM,   // 32px
    controlHeightLG: controlHeightTokens.controlHeightXL,   // 44px

    // ============================================
    // BORDER RADIUS — threaded onto the concentric semantic scale (radiusTokens)
    // ============================================
    // AntD primitives round on the new scale. XS stays 2 (AntD arrow radius has no
    // semantic rung). borderRadiusLG moves 11 → panel (12) so cards/modals/popovers
    // round concentrically with the elevation set.
    borderRadius: radiusTokens.card,                     // 8px (card rung — default)
    borderRadiusLG: radiusTokens.panel,                  // 12px (panel rung — was 11)
    borderRadiusSM: radiusTokens.control,                // 6px (control rung)
    borderRadiusXS: borderRadiusTokens.borderRadiusXS,   // 2px (AntD arrow — kept)

    // ============================================
    // SHADOWS
    // ============================================
    boxShadow: shadowTokens.boxShadow,
    boxShadowSecondary: shadowTokens.boxShadowSM,
  },

  // ============================================
  // COMPONENT-SPECIFIC STYLES
  // ============================================
  components: {
    Layout: {
      headerBg: neutralColors.light.bgContainer,          // #ffffff
      headerColor: neutralColors.light.textPrimary,
      bodyBg: neutralColors.light.bgLayout,               // #f5f5f5
      siderBg: neutralColors.light.bgContainer,           // #ffffff
      // AntD derives `headerHeight` as `controlHeight * 2`
      // (`antd/es/layout/style/index.js:93`), so pointing the control seed at the
      // house scale (24 -> 36) grows the app header 48 -> 72px on EVERY screen.
      // Pinned to the shipped 48 so the seed move stays ONE class of change —
      // control heights — instead of silently redesigning the app chrome.
      // NOTE: 48 was itself a by-product of the old (wrong) seed, never a design
      // decision; AntD stock is 64. Changing it is a one-line owner call, here
      // and in the other theme block.
      headerHeight: 48,
    },

    Menu: {
      itemBg: 'transparent',
      itemColor: neutralColors.light.textPrimary,
      itemHoverBg: 'rgba(0, 0, 0, 0.06)',
      itemHoverColor: purpleColors.purple6,                // Purple on hover
      itemSelectedBg: purpleColors.purple1,                // Light purple background
      itemSelectedColor: purpleColors.purple6,             // Purple text when selected
      itemActiveBg: purpleColors.purple2,                  // Active background
      // AntD derives the icon-to-label gap as `controlHeightSM - fontSize`
      // (`antd/es/menu/style/index.js:443`). That is an accident of the seed, not a
      // spacing decision: with the old (wrong) controlHeightSM of 16 it computed to
      // **2px** — the icon sat glued to its label — and moving the seed to the house
      // control scale (32) pushed it to **18px**, which measured 19px of ellipsis on
      // the vendor / customer detail side rails (all four labels truncated, where
      // only one was before). Pinned to the spacing scale so a control-height token
      // can never decide a margin again.
      iconMarginInlineEnd: marginTokens.marginXS,          // 8px
    },

    Button: {
      borderRadius: radiusTokens.card,                     // 8px (card rung)
      // Same miswiring as `Select` below: AntD's large variant rounds on
      // `borderRadiusLG`, not on `borderRadius`, so the 8px above was silently
      // ignored at size="large" and every large Button rendered the panel rung
      // (12). Measured 2026-09-01 on the Add Price Band modal: Cancel/Save at
      // r12 with NO inline radius, sitting against fields at r8 — a visible 4px
      // mismatch in the operator's own screenshot.
      borderRadiusLG: radiusTokens.card,                   // 8px — large Button
      primaryColor: '#ffffff',                             // White text on purple
      primaryShadow: 'none',                               // No shadow
      defaultShadow: 'none',                               // No shadow for default buttons
    },

    Table: {
      borderRadius: radiusTokens.control,                 // 6px (control rung)
      headerBg: neutralColors.light.bgSpotlight,          // #fafafa
      headerColor: neutralColors.light.textPrimary,
      rowHoverBg: purpleColors.purple1,                   // Light purple on hover
    },

    Card: {
      borderRadius: radiusTokens.panel,                   // 12px (panel rung — was 11)
      boxShadow: shadowTokens.boxShadowMD,                // de-border elevation (light)
      boxShadowSecondary: shadowTokens.boxShadowSM,
    },

    Input: {
      borderRadius: radiusTokens.card,                    // 8px (card rung)
      // Same miswiring again (`input/style/index.js` genInputLargeStyle reads
      // `borderRadiusLG`). Large Inputs only LOOKED correct because
      // `formStyles.inputStyle` forces `borderRadius: 8` inline at ~339 call
      // sites; strip the inline and the corner is 12. Measured 2026-09-01 with a
      // hash-scoped synthetic element: unforced `.ant-input-lg` computes 12px.
      borderRadiusLG: radiusTokens.card,                  // 8px — large Input
    },

    Select: {
      borderRadius: radiusTokens.card,                    // 8px (card rung)
      // AntD rounds `.ant-select-selector` on `borderRadiusLG` for size="large"
      // (`antd/es/select/style/single.js` genSingleStyle -> mergeToken({borderRadius:
      // token.borderRadiusLG}), and the same in multiple.js largeToken), NOT on the
      // `borderRadius` above. With the global borderRadiusLG at the panel rung (12)
      // the stated 8px intent was silently ignored on every large Select, so a Select
      // beside an Input in the same row showed a different corner. Scoped to Select
      // ONLY — the global alias is left at 12 because 31 other AntD components read it.
      borderRadiusLG: radiusTokens.card,                  // 8px — large Select selector
    },

    Modal: {
      borderRadius: radiusTokens.panel,                   // 12px (panel rung — was 11)
      boxShadow: shadowTokens.boxShadowLG,                // floats higher than a card
      boxShadowSecondary: shadowTokens.boxShadowMD,
    },

    Popover: {
      borderRadius: radiusTokens.panel,                   // 12px (panel rung)
      boxShadow: shadowTokens.boxShadowMD,                // de-border elevation (light)
      boxShadowSecondary: shadowTokens.boxShadowSM,
    },

    Drawer: {
      borderRadius: radiusTokens.panel,                   // 12px (panel rung — was 11)
    },

    Tag: {
      borderRadius: radiusTokens.control,                 // 6px (control rung)
    },

    Badge: {
      colorPrimary: purpleColors.purple6,                 // Purple badges
    },

    Alert: {
      colorInfoBg: purpleColors.purple1,                  // Info alerts use purple
      colorInfoBorder: purpleColors.purple3,
    },

    Progress: {
      defaultColor: purpleColors.purple6,                 // Purple progress bars
    },

    Switch: {
      colorPrimary: purpleColors.purple6,                 // Purple switches
    },

    Radio: {
      colorPrimary: purpleColors.purple6,                 // Purple radio buttons
    },

    Checkbox: {
      colorPrimary: purpleColors.purple6,                 // Purple checkboxes
    },

    Tabs: {
      itemActiveColor: purpleColors.purple6,              // Active tab is purple
      itemHoverColor: purpleColors.purple5,
      itemSelectedColor: purpleColors.purple6,
      inkBarColor: purpleColors.purple6,                  // Tab indicator is purple
    },

    Tooltip: {
      // Light mode: Light background with black text for better readability
      colorBgSpotlight: '#ffffff',                        // White tooltip background
      colorTextLightSolid: 'rgba(0, 0, 0, 0.88)',        // Black text on light background
    },
  },
};

/**
 * Dark Theme Configuration
 * Purple colors adjusted for dark mode
 */
export const darkTheme: ThemeConfig = {
  token: {
    // ============================================
    // PURPLE PRIMARY COLOR SYSTEM (Dark Mode)
    // ============================================
    colorPrimary: purpleColorsDark.purple6,              // Same base purple

    colorPrimaryBg: purpleColorsDark.purple1,
    colorPrimaryBgHover: purpleColorsDark.purple2,
    colorPrimaryBorder: purpleColorsDark.purple3,
    colorPrimaryBorderHover: purpleColorsDark.purple4,
    colorPrimaryHover: purpleColorsDark.purple5,
    colorPrimaryActive: purpleColorsDark.purple7,
    colorPrimaryTextHover: purpleColorsDark.purple7,
    colorPrimaryText: purpleColorsDark.purple6,
    colorPrimaryTextActive: purpleColorsDark.purple8,

    // ============================================
    // INFO & LINK COLORS → PURPLE (Dark Mode)
    // ============================================
    colorInfo: purpleColorsDark.purple6,
    colorInfoBg: purpleColorsDark.purple1,
    colorInfoBgHover: purpleColorsDark.purple2,
    colorInfoBorder: purpleColorsDark.purple3,
    colorInfoBorderHover: purpleColorsDark.purple4,
    colorInfoHover: purpleColorsDark.purple5,
    colorInfoActive: purpleColorsDark.purple7,
    colorInfoTextHover: purpleColorsDark.purple7,
    colorInfoText: purpleColorsDark.purple6,
    colorInfoTextActive: purpleColorsDark.purple8,

    colorLink: purpleColorsDark.purple6,
    colorLinkHover: purpleColorsDark.purple7,
    colorLinkActive: purpleColorsDark.purple8,

    // ============================================
    // STATUS COLORS (Same as light mode)
    // ============================================
    colorSuccess: statusColors.success,
    colorWarning: statusColors.warning,
    colorError: statusColors.error,

    // ============================================
    // BACKGROUND COLORS - Dark Mode
    // ============================================
    colorBgContainer: neutralColors.dark.bgContainer,      // #141414
    colorBgElevated: neutralColors.dark.bgElevated,        // #1f1f1f
    colorBgLayout: neutralColors.dark.bgLayout,            // #000000
    colorBgSpotlight: neutralColors.dark.bgSpotlight,      // #262626

    // ============================================
    // TEXT COLORS - Dark Mode
    // ============================================
    colorText: neutralColors.dark.textPrimary,            // rgba(255, 255, 255, 0.85)
    colorTextSecondary: neutralColors.dark.textSecondary, // rgba(255, 255, 255, 0.65)
    colorTextTertiary: neutralColors.dark.textTertiary,   // rgba(255, 255, 255, 0.45)
    colorTextQuaternary: neutralColors.dark.textQuaternary, // rgba(255, 255, 255, 0.25)

    // ============================================
    // BORDER COLORS - Dark Mode
    // ============================================
    colorBorder: neutralColors.dark.border,               // #434343
    colorBorderSecondary: neutralColors.dark.borderSecondary, // #303030

    // ============================================
    // TYPOGRAPHY (Same as light mode)
    // ============================================
    fontSize: typographyTokens.sm.size,
    fontSizeHeading1: typographyTokens.h1.size,
    fontSizeHeading2: typographyTokens.h2.size,
    fontSizeHeading3: typographyTokens.h3.size,
    fontSizeHeading4: typographyTokens.h4.size,
    fontSizeHeading5: typographyTokens.h5.size,

    lineHeight: typographyTokens.sm.lineHeight / typographyTokens.sm.size,
    lineHeightHeading1: typographyTokens.h1.lineHeight / typographyTokens.h1.size,
    lineHeightHeading2: typographyTokens.h2.lineHeight / typographyTokens.h2.size,
    lineHeightHeading3: typographyTokens.h3.lineHeight / typographyTokens.h3.size,
    lineHeightHeading4: typographyTokens.h4.lineHeight / typographyTokens.h4.size,
    lineHeightHeading5: typographyTokens.h5.lineHeight / typographyTokens.h5.size,

    fontSizeSM: typographyTokens.sm.size,
    fontSizeLG: typographyTokens.lg.size,
    fontSizeXL: typographyTokens.h4.size,

    // ============================================
    // SPACING (Same as light mode)
    // ============================================
    paddingXXS: paddingTokens.paddingXXS,
    paddingXS: paddingTokens.paddingXS,
    paddingSM: paddingTokens.paddingSM,
    padding: paddingTokens.padding,
    paddingMD: paddingTokens.paddingMD,
    paddingLG: paddingTokens.paddingLG,
    paddingXL: paddingTokens.paddingXL,

    marginXXS: marginTokens.marginXXS,
    marginXS: marginTokens.marginXS,
    marginSM: marginTokens.marginSM,
    margin: marginTokens.margin,
    marginMD: marginTokens.marginMD,
    marginLG: marginTokens.marginLG,
    marginXL: marginTokens.marginXL,
    marginXXL: marginTokens.marginXXL,

    // ============================================
    // SIZING (Same as light mode)
    // ============================================
    sizeXXS: sizeTokens.sizeXXS,
    sizeXS: sizeTokens.sizeXS,
    sizeSM: sizeTokens.sizeSM,
    size: sizeTokens.size,
    sizeMS: sizeTokens.sizeMS,
    sizeMD: sizeTokens.sizeMD,
    sizeLG: sizeTokens.sizeLG,
    sizeXL: sizeTokens.sizeXL,
    sizeXXL: sizeTokens.sizeXXL,

    // Control heights — same house control scale as light (see the light block
    // for why this is NOT `sizeTokens`).
    controlHeight: controlHeightTokens.controlHeight,       // 36px
    controlHeightSM: controlHeightTokens.controlHeightSM,   // 32px
    controlHeightLG: controlHeightTokens.controlHeightXL,   // 44px

    // ============================================
    // BORDER RADIUS (Same scale as light mode — threaded onto radiusTokens)
    // ============================================
    borderRadius: radiusTokens.card,                     // 8px (card rung)
    borderRadiusLG: radiusTokens.panel,                  // 12px (panel rung — was 11)
    borderRadiusSM: radiusTokens.control,                // 6px (control rung)
    borderRadiusXS: borderRadiusTokens.borderRadiusXS,   // 2px (AntD arrow — kept)

    // ============================================
    // SHADOWS (Dark mode specific)
    // ============================================
    boxShadow: shadowTokensDark.boxShadow,
    boxShadowSecondary: shadowTokensDark.boxShadowSM,
  },

  // ============================================
  // COMPONENT-SPECIFIC STYLES (Dark Mode)
  // ============================================
  components: {
    Layout: {
      headerBg: neutralColors.dark.bgContainer,           // #141414
      headerColor: neutralColors.dark.textPrimary,
      bodyBg: neutralColors.dark.bgLayout,                // #000000
      siderBg: neutralColors.dark.bgContainer,            // #141414
      // AntD derives `headerHeight` as `controlHeight * 2`
      // (`antd/es/layout/style/index.js:93`), so pointing the control seed at the
      // house scale (24 -> 36) grows the app header 48 -> 72px on EVERY screen.
      // Pinned to the shipped 48 so the seed move stays ONE class of change —
      // control heights — instead of silently redesigning the app chrome.
      // NOTE: 48 was itself a by-product of the old (wrong) seed, never a design
      // decision; AntD stock is 64. Changing it is a one-line owner call, here
      // and in the other theme block.
      headerHeight: 48,
    },

    Menu: {
      itemBg: 'transparent',
      itemColor: neutralColors.dark.textPrimary,
      itemHoverBg: 'rgba(255, 255, 255, 0.08)',
      itemHoverColor: purpleColorsDark.purple6,           // Purple on hover
      itemSelectedBg: purpleColorsDark.purple1,           // Dark purple background
      itemSelectedColor: purpleColorsDark.purple6,        // Purple text when selected
      itemActiveBg: purpleColorsDark.purple2,
      // See the light block: AntD derives this from `controlHeightSM - fontSize`,
      // so the control-height seed silently moved it 2px -> 18px and ellipsised the
      // detail side-rail labels. Pinned to the spacing scale in both themes.
      iconMarginInlineEnd: marginTokens.marginXS,          // 8px
    },

    Button: {
      borderRadius: radiusTokens.card,                     // 8px (card rung)
      // Same miswiring as `Select` below: AntD's large variant rounds on
      // `borderRadiusLG`, not on `borderRadius`, so the 8px above was silently
      // ignored at size="large" and every large Button rendered the panel rung
      // (12). Measured 2026-09-01 on the Add Price Band modal: Cancel/Save at
      // r12 with NO inline radius, sitting against fields at r8 — a visible 4px
      // mismatch in the operator's own screenshot.
      borderRadiusLG: radiusTokens.card,                   // 8px — large Button
      primaryColor: '#ffffff',                              // White text on purple button
      primaryShadow: 'none',                                // No shadow
      defaultShadow: 'none',                                // No shadow for default buttons
    },

    Table: {
      borderRadius: radiusTokens.control,                 // 6px (control rung)
      headerBg: neutralColors.dark.bgElevated,            // #1f1f1f
      headerColor: neutralColors.dark.textPrimary,
      rowHoverBg: purpleColorsDark.purple1,               // Dark purple on hover
    },

    Card: {
      borderRadius: radiusTokens.panel,                   // 12px (panel rung — was 11)
      boxShadow: shadowTokensDark.boxShadowMD,            // de-border elevation (dark)
      boxShadowSecondary: shadowTokensDark.boxShadowSM,
    },

    Input: {
      borderRadius: radiusTokens.card,                    // 8px (card rung)
      // Same miswiring again (`input/style/index.js` genInputLargeStyle reads
      // `borderRadiusLG`). Large Inputs only LOOKED correct because
      // `formStyles.inputStyle` forces `borderRadius: 8` inline at ~339 call
      // sites; strip the inline and the corner is 12. Measured 2026-09-01 with a
      // hash-scoped synthetic element: unforced `.ant-input-lg` computes 12px.
      borderRadiusLG: radiusTokens.card,                  // 8px — large Input
    },

    Select: {
      borderRadius: radiusTokens.card,                    // 8px (card rung)
      // AntD rounds `.ant-select-selector` on `borderRadiusLG` for size="large"
      // (`antd/es/select/style/single.js` genSingleStyle -> mergeToken({borderRadius:
      // token.borderRadiusLG}), and the same in multiple.js largeToken), NOT on the
      // `borderRadius` above. With the global borderRadiusLG at the panel rung (12)
      // the stated 8px intent was silently ignored on every large Select, so a Select
      // beside an Input in the same row showed a different corner. Scoped to Select
      // ONLY — the global alias is left at 12 because 31 other AntD components read it.
      borderRadiusLG: radiusTokens.card,                  // 8px — large Select selector
    },

    Modal: {
      borderRadius: radiusTokens.panel,                   // 12px (panel rung — was 11)
      boxShadow: shadowTokensDark.boxShadowLG,            // floats higher than a card
      boxShadowSecondary: shadowTokensDark.boxShadowMD,
    },

    Popover: {
      borderRadius: radiusTokens.panel,                   // 12px (panel rung)
      boxShadow: shadowTokensDark.boxShadowMD,            // de-border elevation (dark)
      boxShadowSecondary: shadowTokensDark.boxShadowSM,
    },

    Drawer: {
      borderRadius: radiusTokens.panel,                   // 12px (panel rung — was 11)
    },

    Tag: {
      borderRadius: radiusTokens.control,                 // 6px (control rung)
    },

    Badge: {
      colorPrimary: purpleColorsDark.purple6,
    },

    Alert: {
      colorInfoBg: purpleColorsDark.purple1,
      colorInfoBorder: purpleColorsDark.purple3,
    },

    Progress: {
      defaultColor: purpleColorsDark.purple6,
    },

    Switch: {
      colorPrimary: purpleColorsDark.purple6,
    },

    Radio: {
      colorPrimary: purpleColorsDark.purple6,
    },

    Checkbox: {
      colorPrimary: purpleColorsDark.purple6,
    },

    Tabs: {
      itemActiveColor: purpleColorsDark.purple6,
      itemHoverColor: purpleColorsDark.purple7,
      itemSelectedColor: purpleColorsDark.purple6,
      inkBarColor: purpleColorsDark.purple6,
    },

    Tooltip: {
      // Dark mode: Slightly lighter dark background with white text
      colorBgSpotlight: 'rgba(255, 255, 255, 0.15)',       // Lighter tooltip background for dark mode
      colorTextLightSolid: '#ffffff',                       // White text
    },
  },
};

// Default export for backward compatibility
export const themeConfig = lightTheme;
